import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import './claim-reserve-table.scss';
import {
  NewConvergeButton,
  FormNumberInputAdornmentField,
} from '../../../../presentation/components';
import { checkPositiveNumber } from '@converge/utils';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandedPaymentRow from './expanded-payment-row';
import { AddPaymentModal } from './add-payment-modal';
import { ReservePayment, ReserveTypeCategories } from '@converge/graphql';
import { useOutletContext } from 'react-router-dom';
import { NumberFormatValues } from 'react-number-format';
import { IProps } from '../claim-entity.detail.overview';

interface MockReserveType {
  name: ReserveTypeCategories;
  category: string;
  uuid?: string;
}

interface MockReserve {
  uuid: string;
  reserveType: MockReserveType;
  budget: number;
  paymentTotal: number;
  reservePayments: ReservePayment[];
  remainingReserve: number;
  remainingRetention?: number; // Remaining retention amount
  totalReserveAmount?: number; // Total reserve amount
}

interface ClaimReserveTableProps {
  reserves: MockReserve[];
  isUlaeReserve?: boolean;
  formatDollarAmount: (amount: number) => string;
  handleAddPayment?: (reserveId: string) => void;
  onSaveChanges?: (
    reserveId: string,
    updatedData: Partial<MockReserve>,
    reserveUuid?: string,
    onSaveComplete?: () => void
  ) => void;
  savingReserveId?: string; // ID of the reserve currently being saved
  isLoading?: boolean; // Loading state from upsert mutation
}

const ClaimReserveTable: React.FC<ClaimReserveTableProps> = ({
  reserves,
  isUlaeReserve,
  formatDollarAmount,
  handleAddPayment,
  onSaveChanges,
  savingReserveId = '', // ID of the reserve currently being saved, empty string if none
  isLoading = false, // Loading state from upsert mutation
}) => {
  // Get claim entity context to access quote limit
  const { claimEntity, isNotActive } = useOutletContext<IProps>();

  const roundToTwoDecimals = (value: number): number =>
    Math.round(value * 100) / 100;

  const validateReserveAmount = (
    values: NumberFormatValues,
    fieldName?: string
  ) => {
    // Handle undefined or null values gracefully
    if (
      !values ||
      (values.value === undefined && values.floatValue === undefined) ||
      values.value === ''
    ) {
      // Allow empty values (they will be caught by required validation if needed)
      if (fieldName) {
        setValidationErrors((prev) => {
          const updated = { ...prev };
          delete updated[fieldName];
          return updated;
        });
      }
      return true;
    }

    // Ensure value is defined for checkPositiveNumber
    const safeValues = {
      ...values,
      value: values.value || '', // Provide empty string if value is undefined
    };

    // First check if it's a positive number
    const isValidPositive = checkPositiveNumber(safeValues);
    if (!isValidPositive) {
      if (fieldName) {
        setValidationErrors((prev) => ({
          ...prev,
          [fieldName]: 'Payment amount is required',
        }));
      }
      return false;
    }

    // Get the original reserve amount from the reserves data (doesn't change with edits)
    const reserveTypeUuid = fieldName?.split('-remainingReserve')[0] || '';
    const originalReserve = reserves.find(
      (r) => r.reserveType?.uuid === reserveTypeUuid
    );
    const originalReserveAmount = originalReserve?.remainingReserve || 0;

    // Get the current reserve amount being entered
    const currentReserveAmount =
      typeof values.floatValue === 'number' ? values.floatValue : 0;

    // Check if combined total (current + others) exceeds quote limit
    const exceedsLimit =
      currentReserveAmount >
      (claimEntity?.remainingLimit || 0) + originalReserveAmount;

    if (
      exceedsLimit &&
      fieldName &&
      originalReserve?.reserveType?.name !== ReserveTypeCategories.Ulae
    ) {
      setValidationErrors((prev) => ({
        ...prev,
        [fieldName]: `Amount cannot exceed available limit of ${formatDollarAmount(
          originalReserveAmount + (claimEntity?.remainingLimit || 0)
        )}`,
      }));
      return false;
    }

    // Clear error if validation passes
    if (fieldName) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }

    return !exceedsLimit;
  };

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedReserveId, setSelectedReserveId] = useState<string>('');
  const [selectedReserveType, setSelectedReserveType] = useState<string>('');
  const [selectedReserveTypeUuid, setSelectedReserveTypeUuid] =
    useState<string>('');
  const [selectedPaymentId, setSelectedPaymentId] = useState<string>('');
  const [isEditingPayment, setIsEditingPayment] = useState<boolean>(false);

  // State for tracking edit mode by field and reserve ID
  const [editModes, setEditModes] = useState<Record<string, boolean>>({});

  // State for tracking validation errors
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Function to generate a unique ID for each field
  const getFieldId = (reserveId: string, fieldName: string) =>
    `${reserveId}-${fieldName}`;

  // Initialize form with default values from reserves
  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: reserves.reduce((acc, reserve) => {
      const reserveTypeUuid = reserve?.reserveType?.uuid || '';
      acc[`${reserveTypeUuid}-remainingReserve`] = roundToTwoDecimals(
        reserve.remainingReserve
      );
      return acc;
    }, {} as Record<string, number>),
  });

  const {
    watch,
    setValue,
    getValues,
    formState: { dirtyFields },
  } = formMethods;

  // Update form values when reserves change from parent
  useEffect(() => {
    reserves.forEach((reserve) => {
      const reserveTypeUuid = reserve?.reserveType?.uuid || '';
      const fieldName = `${reserveTypeUuid}-remainingReserve`;

      // Only update if the field isn't dirty (user hasn't edited it)
      if (!formMethods.formState.dirtyFields[fieldName]) {
        setValue(fieldName, roundToTwoDecimals(reserve.remainingReserve));
      }
    });
  }, [reserves, setValue, formMethods.formState.dirtyFields]);

  useEffect(() => {
    const rowsWithPayments: Record<string, boolean> = {};

    reserves.forEach((reserve) => {
      const accordionKey = reserve.reserveType?.uuid || reserve.uuid;
      const hasPayments =
        reserve.reservePayments && reserve.reservePayments.length > 0;

      if (hasPayments) {
        rowsWithPayments[accordionKey] = true;
      }
    });

    if (Object.keys(rowsWithPayments).length > 0) {
      setExpandedRows((prev) => ({
        ...prev,
        ...rowsWithPayments,
      }));
    }
  }, [reserves]);

  // Watch all form values to update the UI
  const formValues = watch();

  // Calculate total current reserves (including any form changes)
  const currentTotalReserves = reserves.reduce((total, reserve) => {
    const reserveTypeUuid = reserve.reserveType?.uuid || '';
    const formFieldName = `${reserveTypeUuid}-remainingReserve`;
    const reserveAmount =
      formValues[formFieldName] !== undefined
        ? Number(formValues[formFieldName])
        : reserve.remainingReserve;
    return total + (reserveAmount || 0);
  }, 0);

  // Toggle edit mode for a field - only remainingReserve is editable
  const toggleEditMode = (reserveId: string, fieldName: string) => {
    // Only allow toggling edit mode for remainingReserve
    if (fieldName !== 'remainingReserve') return;

    const fieldId = getFieldId(reserveId, fieldName);
    const isCurrentlyEditing = editModes[fieldId];
    const formFieldName = `${reserveId}-${fieldName}`;

    // When entering edit mode, make sure we use current value
    if (!isCurrentlyEditing) {
      // Get current value from form
      const currentValue = getValues(formFieldName);

      // If no value is set yet, initialize with reserve value
      if (currentValue === undefined || currentValue === null) {
        const reserve = reserves.find((r) => r.reserveType?.uuid === reserveId);
        if (reserve) {
          setValue(
            formFieldName,
            roundToTwoDecimals(reserve.remainingReserve),
            {
              shouldDirty: false,
            }
          );
        }
      }
    }

    // Toggle edit mode
    setEditModes((prev) => ({
      ...prev,
      [fieldId]: !prev[fieldId],
    }));
  };

  const toggleRowExpansion = (id: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Function to handle updating a payment
  const handleUpdatePayment = (paymentId: string) => {
    setSelectedPaymentId(paymentId);
    setIsEditingPayment(true);
    setIsModalOpen(true);
  };

  // Function to refresh data after payment updates or deletions
  const handleRefreshData = () => {
    // If there's a parent callback to refresh data, call it
    if (onSaveChanges) {
      onSaveChanges(selectedReserveId, {}, selectedReserveId);
    }
  };

  // Function to handle Add Payment button click
  const handleAddPaymentClick = (reserve: MockReserve) => {
    setSelectedReserveType(reserve.reserveType.name || '');
    setSelectedReserveTypeUuid(reserve.reserveType.uuid || '');
    setSelectedReserveId(reserve.uuid || '');
    setIsModalOpen(true);
    if (handleAddPayment) {
      handleAddPayment(reserve.uuid);
    }
  };

  const handleSaveChangesClick = (
    reserveTypeUuid: string,
    reserve: MockReserve
  ) => {
    if (onSaveChanges) {
      const value = formValues[`${reserveTypeUuid}-remainingReserve`];

      const fieldName =
        `${reserveTypeUuid}-remainingReserve` as `${string}.remainingReserve`;
      // Clear the dirty field by setting the value without marking as dirty
      formMethods.resetField(fieldName);
      // Call the save changes handler with the updated value and callback
      onSaveChanges(
        reserveTypeUuid,
        { remainingReserve: Number(value) },
        reserve.uuid,
        () => {
          // Close edit mode if it's still open
          const fieldId = getFieldId(reserveTypeUuid, 'remainingReserve');
          if (editModes[fieldId]) {
            setEditModes((prev) => ({
              ...prev,
              [fieldId]: false,
            }));
          }
        }
      );
    }
  };

  // Function to handle Pay Now button click in expanded row
  const handlePayNowClick = (reserve: MockReserve) => {
    setSelectedReserveId(reserve.uuid);
    setSelectedReserveType(reserve.reserveType.name);
    setIsEditingPayment(false);
    setIsModalOpen(true);
    if (handleAddPayment) {
      handleAddPayment(reserve.uuid);
    }
  };

  if (!reserves || reserves.length === 0) {
    return (
      <Box p={2} textAlign="center">
        <Typography>No reserves data found</Typography>
      </Box>
    );
  }

  const renderTableHeader = () => (
    <TableHead>
      <TableRow>
        <TableCell
          className="table-cell"
          sx={{ width: isUlaeReserve ? '33%' : '18%' }}
        >
          RESERVE TYPE
        </TableCell>
        {!isUlaeReserve && (
          <TableCell className="table-cell" sx={{ width: '13%' }}>
            RETENTION PAID
          </TableCell>
        )}
        <TableCell className="table-cell" sx={{ width: '16%' }}>
          REMAINING RESERVE
        </TableCell>
        <TableCell className="table-cell" sx={{ width: '13%' }}>
          RESERVE PAID
        </TableCell>
        <TableCell className="table-cell" sx={{ width: '13%' }}>
          TOTAL INCURRED
        </TableCell>
        <TableCell className="table-cell" sx={{ width: '15%' }}></TableCell>
        <TableCell className="table-cell" sx={{ width: '15%' }}></TableCell>
      </TableRow>
    </TableHead>
  );

  return (
    <FormProvider {...formMethods}>
      <>
        <TableContainer className="reserve-table">
          <Table
            aria-label="reserve table"
            sx={{
              tableLayout: 'fixed',
              borderCollapse: 'collapse',
              borderSpacing: 0,
            }}
          >
            {renderTableHeader()}
            <TableBody>
              {reserves.map((reserve) => {
                const accordionKey = reserve.reserveType?.uuid || reserve.uuid;
                const isExpanded = expandedRows[accordionKey] || false;
                const reserveTypeUuid = reserve?.reserveType?.uuid || '';

                const payments = reserve.reservePayments
                  ? reserve.reservePayments.map((payment) => {
                      // Preserve the original fields but also add mapped versions for the payment row
                      return {
                        ...payment, // Keep all original fields
                        id: payment.uuid || '',
                        reserveId: reserve.uuid,
                        date: payment.createdAt || '',
                        paymentDate: payment.createdAt || null, // Date initiated
                        paidAt: payment.paidAt || null, // Date paid
                        retentionAmount: payment.retentionAmount || null, // Retention amount
                        reserveAmount: payment.reserveAmount || null, // Reserve amount
                        invoiceNumber: payment.invoiceNumber || null, // Invoice number
                        vendor: payment.vendor || undefined, // Vendor object for complex mapping
                        status: payment.status || 'OPEN',
                        paymentMethod: payment.type || '',
                        insured: payment.insured,
                      };
                    })
                  : [];
                const remainingReserveFieldId = getFieldId(
                  reserveTypeUuid,
                  'remainingReserve'
                );

                const currentRemainingReserve =
                  formValues[remainingReserveFieldId] !== undefined
                    ? Number(formValues[remainingReserveFieldId])
                    : reserve.remainingReserve;

                const isSaveChangesDisabled =
                  (isLoading && savingReserveId === reserveTypeUuid) ||
                  !dirtyFields[`${reserveTypeUuid}-remainingReserve`] ||
                  validationErrors[`${reserveTypeUuid}-remainingReserve`] !==
                    undefined;

                const disabledAddPayment =
                  (reserve?.reserveType?.name === 'ULAE' &&
                    (!reserve.uuid || currentRemainingReserve === 0)) ||
                  (isLoading && savingReserveId === reserveTypeUuid) ||
                  !isSaveChangesDisabled;

                return (
                  <React.Fragment key={accordionKey}>
                    {/* {
                      <TableRow className="row-spacer">
                        <TableCell colSpan={7}></TableCell>
                      </TableRow>
                    } */}
                    <TableRow
                      className={`data-row ${
                        isExpanded ? '' : 'gray-border-bottom'
                      }`}
                      sx={{
                        '&:hover': { backgroundColor: '#d9f9f5' },
                      }}
                    >
                      <TableCell sx={{ width: '18%' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton
                            size="small"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginBottom: '0px !important',
                            }}
                            onClick={() => toggleRowExpansion(accordionKey)}
                            aria-label="expand row"
                          >
                            {isExpanded ? (
                              <KeyboardArrowDownIcon
                                sx={{
                                  height: '22px',
                                  width: '22px',
                                  '& svg': {
                                    width: '22px',
                                    height: '22px',
                                  },
                                }}
                              />
                            ) : (
                              <KeyboardArrowRightIcon
                                sx={{
                                  height: '22px',
                                  width: '22px',
                                  '& svg': {
                                    width: '22px',
                                    height: '22px',
                                  },
                                }}
                              />
                            )}
                          </IconButton>
                          <Typography className="table-cell">
                            {reserve.reserveType.name === 'LOSS'
                              ? 'Loss'
                              : reserve.reserveType.name}
                          </Typography>
                        </div>
                      </TableCell>
                      {(reserve.reserveType?.name ===
                        ReserveTypeCategories.Alae ||
                        reserve.reserveType?.name ===
                          ReserveTypeCategories.Loss) && (
                        <TableCell sx={{ width: '13%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography className="table-cell">
                              {formatDollarAmount(
                                reserve.reservePayments?.reduce(
                                  (total, payment) =>
                                    total + (payment.retentionAmount || 0),
                                  0
                                ) || 0
                              )}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell sx={{ width: '13%' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            width: '100%',
                          }}
                        >
                          {!isNotActive ? (
                            <>
                              <FormNumberInputAdornmentField
                                name={`${reserveTypeUuid}-remainingReserve`}
                                // Use defaultValue to ensure initial value is set
                                marginRight="-10px"
                                containerWidth={12}
                                adornmentText="$"
                                thousandSeparator=","
                                isAllowed={(values) => {
                                  const isPositive =
                                    checkPositiveNumber(values);
                                  const fieldKey = `${reserveTypeUuid}-remainingReserve`;
                                  validateReserveAmount(values, fieldKey);

                                  return isPositive;
                                }}
                                placeholder="0"
                                onBlur={() => {
                                  toggleEditMode(
                                    reserveTypeUuid,
                                    'remainingReserve'
                                  );
                                }}
                              />
                              {validationErrors[
                                `${reserveTypeUuid}-remainingReserve`
                              ] && (
                                <Typography
                                  variant="caption"
                                  className="remaining-reserve"
                                >
                                  {
                                    validationErrors[
                                      `${reserveTypeUuid}-remainingReserve`
                                    ]
                                  }
                                </Typography>
                              )}
                            </>
                          ) : (
                            <Typography
                              className="table-cell editable-field"
                              onClick={() =>
                                toggleEditMode(
                                  reserveTypeUuid,
                                  'remainingReserve'
                                )
                              }
                            >
                              {formatDollarAmount(
                                formValues[
                                  `${reserveTypeUuid}-remainingReserve`
                                ]
                              )}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: '13%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography className="table-cell">
                            {formatDollarAmount(reserve.paymentTotal)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: '13%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography className="table-cell">
                            {formatDollarAmount(reserve.budget)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ width: '15%' }}>
                        <NewConvergeButton
                          label="ADD PAYMENT"
                          onClick={() => handleAddPaymentClick(reserve)}
                          className={`save-changes-button contained-design ${
                            disabledAddPayment ? 'disabled-button' : ''
                          }`}
                          size="small"
                          disabled={disabledAddPayment || isNotActive}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ width: '15%' }}>
                        <NewConvergeButton
                          label={
                            isLoading && savingReserveId === reserveTypeUuid
                              ? 'SAVING...'
                              : 'SAVE CHANGES'
                          }
                          onClick={() =>
                            handleSaveChangesClick(reserveTypeUuid, reserve)
                          }
                          className="save-changes-button contained-design"
                          size="small"
                          loading={
                            isLoading && savingReserveId === reserveTypeUuid
                          } // Show loading when mutation is active for this specific reserve
                          disabled={isSaveChangesDisabled}
                        />
                      </TableCell>
                    </TableRow>

                    {/* Expanded row content */}
                    {isExpanded && (
                      <TableRow
                        className="expanded-row"
                        sx={{
                          backgroundColor: '#f9f9f9',
                          borderBottom: '1px solid #eaeaea',
                          '&:hover': { backgroundColor: '#f9f9f9' },
                        }}
                      >
                        <TableCell colSpan={7} sx={{ py: 2 }}>
                          <ExpandedPaymentRow
                            payments={payments}
                            formatCurrency={formatDollarAmount}
                            isNotActive={isNotActive}
                            onPayNow={() => handlePayNowClick(reserve)}
                            onUpdatePayment={(paymentId) => {
                              setSelectedReserveId(reserve.uuid);
                              setSelectedReserveTypeUuid(
                                reserve?.reserveType?.uuid || ''
                              );
                              setSelectedReserveType(reserve.reserveType.name);
                              handleUpdatePayment(paymentId);
                            }}
                            onRefreshData={handleRefreshData}
                            reserveUuid={reserve.uuid}
                            disablePayNow={!reserve.uuid}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Spacer before footer */}

              {/* Footer row */}
              <TableRow className="footer-row">
                <TableCell sx={{ width: '18%' }}>
                  <Typography
                    className="payment-total-table-cell"
                    fontWeight={800}
                  >
                    TOTAL
                  </Typography>
                </TableCell>
                {!isUlaeReserve && (
                  <TableCell sx={{ width: '13%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <>
                        <Typography
                          className={`payment-total-table-cell ${
                            claimEntity?.retentionPaymentTotal ===
                            claimEntity?.retention
                              ? 'retention-payment-complete'
                              : 'retention-payment-incomplete'
                          }`}
                          fontWeight={800}
                        >
                          {formatDollarAmount(
                            claimEntity?.retentionPaymentTotal || 0
                          )}
                        </Typography>
                        {claimEntity?.retentionPaymentTotal ===
                          claimEntity?.retention && (
                          <Box className="check-icon-wrapper">
                            <CheckIcon className="check-icon" />
                          </Box>
                        )}
                      </>
                    </Box>
                  </TableCell>
                )}
                <TableCell sx={{ width: '13%' }}>
                  <Typography
                    className="payment-total-table-cell"
                    fontWeight={800}
                  >
                    {formatDollarAmount(currentTotalReserves)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: '13%' }}>
                  <Typography
                    className="payment-total-table-cell"
                    fontWeight={800}
                  >
                    {formatDollarAmount(
                      reserves.reduce((sum, r) => sum + r.paymentTotal, 0)
                    )}
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: '13%' }}>
                  <Typography
                    className="payment-total-table-cell"
                    fontWeight={800}
                  >
                    {formatDollarAmount(
                      reserves.reduce((sum, r) => sum + r.budget, 0)
                    )}
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: '15%' }}></TableCell>
                <TableCell sx={{ width: '15%' }}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add/Edit Payment Modal */}
        <AddPaymentModal
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setIsEditingPayment(false);
            setSelectedPaymentId('');
          }}
          onSubmit={(data) => {
            // Ensure the row for the reserve that had payment added stays expanded
            const reserveToExpand = reserves.find(
              (r) => r.uuid === selectedReserveId
            );
            if (reserveToExpand) {
              const accordionKey =
                reserveToExpand.reserveType?.uuid || reserveToExpand.uuid;
              setExpandedRows((prev) => ({
                ...prev,
                [accordionKey]: true,
              }));
            }

            setIsModalOpen(false);
            setIsEditingPayment(false);
            setSelectedPaymentId('');
            // Refresh data after submission
            handleRefreshData();
          }}
          reserveType={selectedReserveType}
          reserveTypeUuid={selectedReserveTypeUuid}
          reserveId={selectedReserveId}
          paymentId={isEditingPayment ? selectedPaymentId : undefined}
          isEditing={isEditingPayment}
          reservePayments={
            reserves.find((r) => r.uuid === selectedReserveId)
              ?.reservePayments || []
          }
          remainingReserve={
            reserves.find((r) => r.uuid === selectedReserveId)
              ?.remainingReserve || 0
          }
          remainingRetention={
            reserves.find((r) => r.uuid === selectedReserveId)
              ?.remainingRetention || 0
          }
          totalReserveAmount={
            reserves.find((r) => r.uuid === selectedReserveId)
              ?.totalReserveAmount || 0
          }
        />
      </>
    </FormProvider>
  );
};

export default ClaimReserveTable;
